/**
 * Which kinds of rows the board tab on screen draws — the feed gateway's
 * `board` scope facet (relay/src/feedgw/scopes.ts `BoardKind`).
 *
 * A sport's headline rows are thousands wide (measured on node1 2026-09-21,
 * NFL: 9,426 rows for 17 games — ~5,000 season-long futures and ~2,700
 * alternate spread/total lines, mostly Polymarket rows with no `class`), and
 * the moneyline tab draws ~180 of them. A board that has said nothing gets
 * every kind: fail wide, never dark.
 *
 * Outside React/Zustand on purpose, like feedNeeds/opNeeds — a declaration
 * change must never re-render anything.
 */
export type BoardKind = 'main' | 'lines' | 'futures';
export type BoardKindsSink = (kinds: BoardKind[] | null) => void;

const _sources = new Map<string, readonly BoardKind[]>();
let _sink: BoardKindsSink | null = null;

/** null = every kind (no board has declared, or one declared everything). */
export function currentBoardKinds(): BoardKind[] | null {
  if (_sources.size === 0) return null;
  const out = new Set<BoardKind>();
  for (const kinds of _sources.values()) for (const k of kinds) out.add(k);
  return out.size >= 3 ? null : [...out].sort();
}

export function declareBoardKinds(sourceId: string, kinds: readonly BoardKind[] | null): void {
  if (kinds == null) _sources.delete(sourceId);
  else _sources.set(sourceId, kinds);
  _sink?.(currentBoardKinds());
}

export function setBoardKindsSink(sink: BoardKindsSink | null): void {
  _sink = sink;
  sink?.(currentBoardKinds());
}

/** The dashboard's market-type tab → the row kinds it draws. `main` rides every
 *  tab: it is small, and it carries the game identity the other tabs hang on. */
export function boardKindsForTab(tab: string): BoardKind[] {
  if (tab === 'series') return ['futures', 'main'];
  if (tab === 'moneyline') return ['main'];
  return ['lines', 'main'];
}
